'use client';

/**
 * useTransactionSimulation Hook
 *
 * Simulates withdrawal transactions before execution to:
 * 1. Verify the transaction will succeed
 * 2. Show users exactly what they'll receive
 * 3. Build trust through transparency
 *
 * Works on any EVM chain (Dogechain, Shibarium, etc.)
 */
import { useState, useCallback } from 'react';
import { simulateContract, readContract } from '@wagmi/core';
import { config } from '@/config/index';
import { formatUnits } from 'viem';
import type { Address } from 'viem';
export interface SimulationAssetChange {
  token: Address;
  symbol: string;
  decimals: number;
  amount: bigint;
  amountFormatted: string;
  direction: 'incoming' | 'outgoing';
}
export interface SimulationResult {
  willSucceed: boolean;
  assetChanges: SimulationAssetChange[];
  warnings: string[];
  gasEstimate?: bigint;
  error?: string;
}
interface UseTransactionSimulationReturn {
  simulate: (params: SimulationParams) => Promise<SimulationResult>;
  result: SimulationResult | null;
  isSimulating: boolean;
  reset: () => void;
}
interface SimulationParams {
  contractAddress: Address;
  poolId: number;
  userAddress: Address;
  lpTokenAddress: Address;
  lpTokenSymbol: string;
  lpTokenDecimals: number;
  stakedAmount: bigint;
  withdrawType: 'standard' | 'emergency';
  abi: any;
  chainId: number;
}

// Standard ERC20 ABI for reading token info
const ERC20_ABI = [{
  inputs: [{
    name: 'account',
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;
export function useTransactionSimulation(): UseTransactionSimulationReturn {
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const simulate = useCallback(async (params: SimulationParams): Promise<SimulationResult> => {
    setIsSimulating(true);
    setResult(null);
    try {
      const {
        contractAddress,
        poolId,
        userAddress,
        lpTokenAddress,
        lpTokenSymbol,
        lpTokenDecimals,
        stakedAmount,
        withdrawType,
        abi,
        chainId
      } = params;

      // Get current LP token balance (to compare after simulation)
      let currentBalance = 0n;
      try {
        currentBalance = await readContract(config, {
          address: lpTokenAddress,
          abi: ERC20_ABI,
          functionName: 'balanceOf',
          args: [userAddress],
          chainId
        });
      } catch {
        // If we can't read balance, continue without it
      }

      // Build simulation args based on withdraw type
      const simulationArgs = withdrawType === 'standard' ? [BigInt(poolId), stakedAmount] : [BigInt(poolId)];

      // Simulate the transaction
      try {
        await simulateContract(config, {
          address: contractAddress,
          abi: [abi],
          functionName: abi.name,
          args: simulationArgs,
          account: userAddress,
          chainId
        });

        // Transaction will succeed!
        const simulationResult_1: SimulationResult = {
          willSucceed: true,
          assetChanges: [{
            token: lpTokenAddress,
            symbol: lpTokenSymbol,
            decimals: lpTokenDecimals,
            amount: stakedAmount,
            amountFormatted: formatUnits(stakedAmount, lpTokenDecimals),
            direction: 'incoming'
          }],
          warnings: withdrawType === 'emergency' ? ['Pending rewards will be forfeited with emergency withdraw'] : []
        };
        setResult(simulationResult_1);
        setIsSimulating(false);
        return simulationResult_1;
      } catch (simError: any) {
        // Transaction will revert
        const errorMessage = simError?.shortMessage || simError?.message || 'Transaction simulation failed';

        // Parse common revert reasons
        let userFriendlyError = errorMessage;
        if (errorMessage.includes('insufficient')) {
          userFriendlyError = 'Insufficient balance in the pool';
        } else if (errorMessage.includes('not owner') || errorMessage.includes('unauthorized')) {
          userFriendlyError = 'You are not authorized to withdraw from this position';
        } else if (errorMessage.includes('already withdrawn') || errorMessage.includes('no stake')) {
          userFriendlyError = 'This position has already been withdrawn or has no stake';
        } else if (errorMessage.includes('paused')) {
          userFriendlyError = 'The contract is currently paused';
        }
        const simulationResult_0: SimulationResult = {
          willSucceed: false,
          assetChanges: [],
          warnings: [],
          error: userFriendlyError
        };
        setResult(simulationResult_0);
        setIsSimulating(false);
        return simulationResult_0;
      }
    } catch (error: any) {
      const simulationResult: SimulationResult = {
        willSucceed: false,
        assetChanges: [],
        warnings: [],
        error: error?.message || 'Failed to simulate transaction'
      };
      setResult(simulationResult);
      setIsSimulating(false);
      return simulationResult;
    }
  }, []);
  const reset = useCallback(() => {
    setResult(null);
    setIsSimulating(false);
  }, []);
  return {
    simulate,
    result,
    isSimulating,
    reset
  };
}